import { useEffect } from 'react';

/**
 * overlayLayer.js — making our own overlays work ON TOP of a Radix Dialog.
 *
 * THE PROBLEM, precisely. While a modal <Dialog/> is open, Radix's
 * DismissableLayer sets `document.body { pointer-events: none }` and re-enables
 * pointer events only on its own portal (see
 * @radix-ui/react-dismissable-layer). Anything of ours rendered outside that
 * portal — the linked-grade drawer, the `$` picker, an image lightbox — is
 * still PAINTED on top, but the pointer goes straight through it to the dialog
 * underneath. The symptom is unmistakable and was reported exactly this way:
 * "I can't close the modal, only the ticket dialog reacts."
 *
 * Two things are needed, and they are together in here so they cannot come
 * apart — one without the other is worse than neither:
 *
 *   pointerEvents: 'auto'   wins hit-testing back from body's `none`. Without
 *                           it the overlay is inert.
 *   data-clara-layer        tells <DialogContent/> that a pointerdown here is
 *                           not really "outside". Without it, the moment the
 *                           overlay becomes clickable, the first click on it
 *                           dismisses the dialog underneath — so the overlay
 *                           would work exactly once, and take its host with it.
 *
 * And a third, from the same root cause: SCROLLING. Radix wraps the dialog in
 * react-remove-scroll, which registers a `wheel`/`touchmove` listener on
 * `document` and preventDefault()s every event whose target is outside the
 * dialog's own subtree — so a list inside our overlay renders a scrollbar and
 * then refuses to move. `useLayerScroll` below fixes that; it is a hook rather
 * than part of layerProps only because it needs the element, not just props.
 *
 * Use `layerProps(name)` on the overlay's outermost fixed element, and
 * `useLayerScroll(ref)` if anything inside it scrolls. Verify with
 * document.elementFromPoint, never with a scripted .click(): a dispatched event
 * ignores pointer-events entirely and will happily "pass" against a dead
 * overlay.
 */

export const LAYER_ATTR = 'data-clara-layer';

/**
 * Spread onto a stacked overlay's root element.
 * @param {string} name  what this layer is, for debugging in the inspector
 * @param {object} [style]  the element's own style, merged (pointerEvents wins)
 */
export const layerProps = (name, style = {}) => ({
  [LAYER_ATTR]: name,
  style: { ...style, pointerEvents: 'auto' },
});

/**
 * Did this Radix outside-interaction actually land on one of our layers?
 * Handles both the CustomEvent Radix passes (detail.originalEvent) and a plain
 * DOM event.
 */
export const isLayerEvent = (e) => {
  const target = e?.detail?.originalEvent?.target ?? e?.target;
  return !!(target && typeof target.closest === 'function' && target.closest(`[${LAYER_ATTR}]`));
};

/** Is any stacked layer currently mounted? Used to decide who owns Escape. */
export const hasOpenLayer = () =>
  typeof document !== 'undefined' && !!document.querySelector(`[${LAYER_ATTR}]`);

/**
 * Let an overlay outside the Radix portal scroll again.
 *
 * react-remove-scroll's blocking listener sits on `document` in the BUBBLE
 * phase (`{ passive: false }`, no capture — see its SideEffect.js). A listener
 * on the element itself therefore runs first, and stopping propagation there
 * means the event never reaches `document` and is never preventDefault()-ed.
 * The browser then scrolls the element normally, which is all we wanted.
 *
 * Deliberately has no dependency array: an overlay like the `$` picker swaps
 * between loading / empty / list roots, so the node under the ref changes
 * between renders. Re-binding a listener on a transient overlay is cheap;
 * being bound to a detached node is not.
 *
 * @param {React.RefObject<HTMLElement>} ref  the overlay's outermost element
 */
export const useLayerScroll = (ref) => {
  useEffect(() => {
    const el = ref?.current;
    if (!el) return undefined;
    const keep = (e) => e.stopPropagation();
    el.addEventListener('wheel', keep, { passive: false });
    el.addEventListener('touchmove', keep, { passive: false });
    return () => {
      el.removeEventListener('wheel', keep);
      el.removeEventListener('touchmove', keep);
    };
  });
};
